import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Students from "./Students";
import Clients from "./Clients";
import Teachers from "./Teachers";
import {
  PEOPLE_TABS,
  loadPeopleViewState,
  savePeopleViewState,
  updatePeopleViewTabState,
} from "./peopleViewState";

const TABS = [
  { key: PEOPLE_TABS.STUDENTS, label: "Uczniowie", Component: Students },
  { key: PEOPLE_TABS.CLIENTS, label: "Klienci", Component: Clients },
  { key: PEOPLE_TABS.TEACHERS, label: "Nauczyciele", Component: Teachers },
];

const getAllowedTabs = (canReadClients, canReadTeachers) => TABS.filter(tab => (
  (tab.key !== PEOPLE_TABS.CLIENTS || canReadClients)
  && (tab.key !== PEOPLE_TABS.TEACHERS || canReadTeachers)
));

const ensureAllowedActiveTab = (state, allowedTabs) => {
  if (allowedTabs.some(tab => tab.key === state.activeTab)) return state;

  return {
    ...state,
    activeTab: allowedTabs[0].key,
  };
};

const loadInitialState = (activeSchoolYearStartYear, initialTab, allowedTabs) => {
  const savedState = loadPeopleViewState(activeSchoolYearStartYear);
  const requestedState = initialTab
    ? { ...savedState, activeTab: initialTab }
    : savedState;

  return ensureAllowedActiveTab(requestedState, allowedTabs);
};

function People({
  initialTab,
  activeSchoolYearStartYear,
  canReadClients,
  canReadTeachers,
}) {
  const allowedTabs = useMemo(
    () => getAllowedTabs(canReadClients, canReadTeachers),
    [canReadClients, canReadTeachers],
  );
  const stateScopeKey = `${activeSchoolYearStartYear}:${initialTab || "default"}:${canReadClients}:${canReadTeachers}`;
  const [scopedPeopleViewState, setScopedPeopleViewState] = useState(() => ({
    scopeKey: stateScopeKey,
    state: loadInitialState(activeSchoolYearStartYear, initialTab, allowedTabs),
  }));
  const peopleViewState = scopedPeopleViewState.state;
  const activeTab = allowedTabs.find(tab => tab.key === peopleViewState.activeTab) || allowedTabs[0];
  const ActiveComponent = activeTab.Component;

  useEffect(() => {
    setScopedPeopleViewState({
      scopeKey: stateScopeKey,
      state: loadInitialState(activeSchoolYearStartYear, initialTab, allowedTabs),
    });
  }, [activeSchoolYearStartYear, allowedTabs, initialTab, stateScopeKey]);

  useEffect(() => {
    if (scopedPeopleViewState.scopeKey !== stateScopeKey) return;
    savePeopleViewState(activeSchoolYearStartYear, peopleViewState);
  }, [activeSchoolYearStartYear, peopleViewState, scopedPeopleViewState.scopeKey, stateScopeKey]);

  const handleTabClick = (tabKey) => {
    setScopedPeopleViewState(currentState => ({
      ...currentState,
      state: updatePeopleViewTabState(currentState.state, tabKey, {}),
    }));
  };

  const handleTabStateChange = (patch) => {
    setScopedPeopleViewState(currentState => ({
      ...currentState,
      state: updatePeopleViewTabState(currentState.state, activeTab.key, patch),
    }));
  };

  return (
    <div className="mb-5">
      <h2 className="manage-title col-block mb-3">Osoby</h2>
      <div className="btn-group mb-3" role="tablist" aria-label="Osoby">
        {allowedTabs.map(tab => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab.key === tab.key}
            className={`btn manage-button ${activeTab.key === tab.key ? "manage-red-button" : "manage-teal-button"}`}
            onClick={() => handleTabClick(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <ActiveComponent
        key={`${scopedPeopleViewState.scopeKey}:${activeTab.key}`}
        peopleViewState={peopleViewState.tabs[activeTab.key]}
        onPeopleViewStateChange={handleTabStateChange}
      />
    </div>
  );
}

People.propTypes = {
  initialTab: PropTypes.oneOf([PEOPLE_TABS.STUDENTS, PEOPLE_TABS.CLIENTS, PEOPLE_TABS.TEACHERS]),
  activeSchoolYearStartYear: PropTypes.number.isRequired,
  canReadClients: PropTypes.bool,
  canReadTeachers: PropTypes.bool,
};

People.defaultProps = {
  initialTab: null,
  canReadClients: true,
  canReadTeachers: true,
};

export default People;
